import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ProfileFormSheet from '../modals/ProfileFormSheet';
import {
  PAYMENT_STATUS,
  TXN_TYPE,
  TXN_KIND,
  TXN_KIND_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  getPaymentStatusMeta,
  getPaymentStatusLabel,
  getTxnKindLabel,
  getTxnTypeLabel,
  formatPaymentAmount,
  formatMonthKeyLabel,
  formatTxnDateTime,
  toDateTimeLocalInput,
  createEmptyTxnDraft,
  computeNetFromTransactions,
  getRemainingDue,
  txnKindToFields,
  draftToTxnKind,
  txnToKind,
  deriveStatusClient,
  draftToProjectedTxn,
  validateDraftTransaction
} from '../../constants/paymentStatuses';
import './PaymentChargeEditor.css';

function txnKindBadgeClass(kind) {
  switch (kind) {
    case TXN_KIND.FULL_PAYMENT:
      return 'pay-editor-txn-badge pay-editor-txn-badge--payment';
    case TXN_KIND.PARTIAL_PAYMENT:
      return 'pay-editor-txn-badge pay-editor-txn-badge--partial';
    case TXN_KIND.REFUND:
      return 'pay-editor-txn-badge pay-editor-txn-badge--refund';
    case TXN_KIND.FAILED_ATTEMPT:
    case TXN_KIND.INSUFFICIENT_FUNDS:
      return 'pay-editor-txn-badge pay-editor-txn-badge--failed_attempt';
    default:
      return 'pay-editor-txn-badge pay-editor-txn-badge--waiver';
  }
}

function InvoiceIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 2h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
      <path d="M14 2v6h6" />
      <path d="M9 13h6M9 17h6M9 9h1" />
    </svg>
  );
}

// Closes the menu on any mousedown outside `ref`. Capture phase so it still fires inside the
// sheet panel, which stops mousedown propagation.
function useOutsideClose(ref, open, setOpen) {
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open, ref, setOpen]);
}

function BadgePicker({ label, badgeClass, options, value, onChange, disabled, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useOutsideClose(wrapRef, open, setOpen);

  return (
    <div className="pay-editor-badge-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`pay-editor-badge-trigger ${badgeClass}`}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        {label}
        <span className="pay-editor-badge-caret" aria-hidden />
      </button>
      {open && (
        <div className="pay-editor-badge-menu" role="listbox" aria-label={ariaLabel}>
          {options.map((opt) => {
            const optValue = opt.value ?? opt.status;
            const isActive = String(optValue) === String(value);
            return (
              <button
                key={optValue}
                type="button"
                role="option"
                aria-selected={isActive}
                className={`pay-editor-badge-option${isActive ? ' is-active' : ''}`}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Modern form-styled dropdown (matches the type picker).
 *
 * The menu is portaled to <body> with fixed positioning so it is never clipped by the sheet's
 * scrollable form body (overflow-y: auto). Closes on outside click; repositions on scroll/resize.
 */
function FieldSelect({ value, options, onChange, disabled, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value)) || options[0];

  const reposition = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.max(r.width, 200);
    const left = Math.min(r.left, window.innerWidth - width - 8);
    setCoords({ left: Math.max(8, left), top: r.bottom + 4, width });
  };

  useEffect(() => {
    if (!open) return undefined;
    reposition();
    const onScrollOrResize = () => reposition();
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    document.addEventListener('mousedown', onDoc, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
      document.removeEventListener('mousedown', onDoc, true);
    };
  }, [open]);

  return (
    <div className="pay-editor-select" ref={wrapRef}>
      <button
        type="button"
        className={`pay-editor-select-trigger${open ? ' is-open' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className="pay-editor-select-value">{selected ? selected.label : 'Select'}</span>
        <span className="pay-editor-select-caret" aria-hidden />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            className="pay-editor-select-menu pay-editor-select-menu--portal"
            role="listbox"
            aria-label={ariaLabel}
            style={{ left: coords.left, top: coords.top, width: coords.width }}
          >
            {options.map((o) => {
              const isActive = String(o.value) === String(value);
              return (
                <button
                  key={o.value || 'none'}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`pay-editor-select-option${isActive ? ' is-active' : ''}`}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}

function RemoveTxnBtn({ onClick, disabled, label = 'Remove transaction' }) {
  return (
    <button
      type="button"
      className="pay-editor-remove-x"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      ×
    </button>
  );
}

function AddTransactionRow({ onClick, disabled }) {
  return (
    <button
      type="button"
      className="pay-editor-add-txn-row"
      onClick={onClick}
      disabled={disabled}
      aria-label="Add transaction"
    >
      <span className="pay-editor-add-txn-row__icon" aria-hidden>
        <span className="pay-editor-add-txn-row__glyph">⇄</span>
        <span className="pay-editor-add-txn-row__plus">+</span>
      </span>
      <span className="pay-editor-add-txn-row__label">Add Transaction</span>
    </button>
  );
}

function TxnRowView({ kind, txnType, amount, occurredOn, createdAt, method, notes, currency, onRemove, showRemove, removing }) {
  const isMoneyKind =
    kind === TXN_KIND.FULL_PAYMENT || kind === TXN_KIND.PARTIAL_PAYMENT || kind === TXN_KIND.REFUND;
  const isDebit = kind === TXN_KIND.REFUND;
  const hasAmount = isMoneyKind && amount != null && amount !== '' && Number.isFinite(Number(amount));
  const methodLabel = PAYMENT_METHOD_OPTIONS.find((o) => o.value === method)?.label || method || '';
  const when = formatTxnDateTime(occurredOn || createdAt);
  const meta = [when, methodLabel && methodLabel !== '—' ? methodLabel : ''].filter(Boolean).join(' · ');

  return (
    <div className="pay-editor-txn-row">
      <div className="pay-editor-txn-row__main">
        <div className="pay-editor-txn-row__line">
          <span className={txnKindBadgeClass(kind)}>
            {kind ? getTxnKindLabel(kind) : getTxnTypeLabel(txnType)}
          </span>
          {hasAmount && (
            <span className={`pay-editor-txn-row__amt${isDebit ? ' pay-editor-txn-row__amt--debit' : ''}`}>
              {isDebit ? '−' : ''}
              {formatPaymentAmount(Math.abs(Number(amount)), currency)}
            </span>
          )}
          {meta ? <span className="pay-editor-txn-row__meta">{meta}</span> : null}
        </div>
        {notes ? <p className="pay-editor-txn-row__notes">{notes}</p> : null}
      </div>
      {showRemove && onRemove ? (
        <RemoveTxnBtn disabled={removing} onClick={onRemove} />
      ) : null}
    </div>
  );
}

function TxnRowEdit({ draft, saving, isAmountMode, remaining, currency, onChange, onFillRemaining }) {
  const kind = draftToTxnKind(draft);
  const isMoneyKind =
    kind === TXN_KIND.FULL_PAYMENT || kind === TXN_KIND.PARTIAL_PAYMENT || kind === TXN_KIND.REFUND;

  return (
    <div className="pay-editor-txn-edit-compact">
      <div className={`pay-editor-txn-edit-line${isMoneyKind ? '' : ' pay-editor-txn-edit-line--no-amt'}`}>
        {isMoneyKind && (
          <div className="pay-editor-txn-edit-cell pay-editor-txn-edit-cell--amt">
            <input
              type="number"
              step="0.01"
              className="profile-sheet-input pay-editor-txn-input"
              value={draft.amount}
              onChange={(e) => onChange({ amount: e.target.value })}
              disabled={saving}
              placeholder={isAmountMode ? '0.00' : 'Amt'}
              aria-label="Amount"
            />
            {remaining != null && remaining > 0 && kind === TXN_KIND.FULL_PAYMENT && (
              <button
                type="button"
                className="pay-editor-fill-link"
                onClick={onFillRemaining}
                disabled={saving}
              >
                Fill
              </button>
            )}
          </div>
        )}
        <div className="pay-editor-txn-edit-cell pay-editor-txn-edit-cell--when">
          <input
            type="datetime-local"
            className="profile-sheet-input pay-editor-txn-input"
            value={toDateTimeLocalInput(draft.occurred_on)}
            onChange={(e) => onChange({ occurred_on: e.target.value })}
            disabled={saving}
            aria-label="Date and time"
          />
        </div>
        <div className="pay-editor-txn-edit-cell pay-editor-txn-edit-cell--method">
          <FieldSelect
            value={draft.method}
            options={PAYMENT_METHOD_OPTIONS}
            onChange={(v) => onChange({ method: v })}
            disabled={saving}
            ariaLabel="Payment method"
          />
        </div>
      </div>
      <input
        type="text"
        className="profile-sheet-input pay-editor-txn-input pay-editor-txn-input--notes"
        value={draft.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
        disabled={saving}
        placeholder="Notes (optional)"
        aria-label="Notes"
      />
    </div>
  );
}

/**
 * Charge editor — invoice-style header with a DERIVED status, plus a ledger of transactions.
 *
 * Status is never set directly: it is computed from the transactions below (mirrors the
 * server's deriveStatus). The only manual override is Waive (marks the charge not applicable).
 * Saving a transaction that would downgrade a settled charge (e.g. a refund that drops the
 * balance below the amount due) asks for confirmation first.
 */
export default function PaymentChargeEditor({
  open,
  onClose,
  saving = false,
  loading = false,
  title,
  subtitle,
  charge,
  canQuickSet = true,
  onAddTransaction,
  onDeleteTransaction
}) {
  const [pendingDraft, setPendingDraft] = useState(null);

  useEffect(() => {
    if (open) setPendingDraft(null);
  }, [open, charge?.requirement_id, charge?.status]);

  const txns = charge?.transactions || [];
  const isAmountMode = charge?.tracking_mode === 'amount';
  const amountDue = charge?.amount_due ?? charge?.amount ?? null;
  const netPaid = charge?.net_paid ?? computeNetFromTransactions(txns);
  const currency = charge?.currency || 'USD';
  const remaining = useMemo(
    () => (isAmountMode ? getRemainingDue(amountDue, netPaid) : null),
    [isAmountMode, amountDue, netPaid]
  );
  const statusMeta = getPaymentStatusMeta(charge?.status);
  const isWaived = charge?.status === PAYMENT_STATUS.NOT_APPLICABLE;
  const chargeName =
    charge?.label || formatMonthKeyLabel(charge?.month_key) || subtitle || 'Charge';
  const waiverTxn = txns.find((t) => t.txn_type === TXN_TYPE.WAIVER);

  // Helper sentence under the title describing what this popup is and how status works.
  const monthLabel = charge?.month_key ? formatMonthKeyLabel(charge.month_key) : '';
  const periodPhrase = monthLabel && monthLabel !== chargeName && monthLabel !== title ? ` for ${monthLabel}` : '';
  const composedSubtext =
    `Below is the “${chargeName}” charge${periodPhrase}, currently ${getPaymentStatusLabel(charge?.status)}. ` +
    'You don’t set the status directly — it’s calculated from the transactions below. ' +
    'Add a payment, refund, or failed attempt and the status updates to match.';

  const startDraft = () => setPendingDraft(createEmptyTxnDraft());

  const updatePending = (patch) => setPendingDraft((d) => ({ ...d, ...patch }));

  const setPendingKind = (kind) => {
    setPendingDraft((d) => {
      const next = { ...d, ...txnKindToFields(kind) };
      // Selecting a full payment in amount mode pre-fills the outstanding balance so it settles.
      if (kind === TXN_KIND.FULL_PAYMENT && isAmountMode && (d.amount === '' || d.amount == null)) {
        if (remaining != null && remaining > 0) next.amount = String(remaining);
      }
      return next;
    });
  };

  const fillRemaining = () => {
    if (remaining == null || remaining <= 0) return;
    updatePending({ ...txnKindToFields(TXN_KIND.FULL_PAYMENT), amount: String(remaining) });
  };

  const draftValidation = useMemo(() => {
    if (!pendingDraft) return null;
    return validateDraftTransaction(pendingDraft, {
      transactions: txns,
      charge: { ...charge, tracking_mode: isAmountMode ? 'amount' : charge.tracking_mode },
      currentStatus: charge?.status,
      currency
    });
  }, [pendingDraft, txns, charge, isAmountMode, currency]);

  const handleSaveDraft = async () => {
    if (!pendingDraft) return;

    const validation = validateDraftTransaction(pendingDraft, {
      transactions: txns,
      charge: { ...charge, tracking_mode: isAmountMode ? 'amount' : charge.tracking_mode },
      currentStatus: charge?.status,
      currency
    });

    if (!validation.canSave) return;

    const selectedKind = draftToTxnKind(pendingDraft);
    const fields = txnKindToFields(selectedKind);
    const payload = {
      ...pendingDraft,
      ...fields,
      amount: pendingDraft.amount === '' ? null : pendingDraft.amount
    };

    if (validation.confirmOnSave) {
      const projected = validation.projected ?? deriveStatusClient(
        [...txns, draftToProjectedTxn(payload, selectedKind)],
        charge
      );
      const confirmed = window.confirm(
        `This transaction changes the status from ${getPaymentStatusLabel(charge?.status)} to ` +
          `${getPaymentStatusLabel(projected)}. Continue?`
      );
      if (!confirmed) return;
    }

    const ok = await onAddTransaction?.(payload);
    if (ok !== false) setPendingDraft(null);
  };

  const handleWaive = async () => {
    const confirmed = window.confirm(
      'Waive this charge? It will be marked Not Applicable for this student. ' +
        'You can remove the waiver later to restore the calculated status.'
    );
    if (!confirmed) return;
    await onAddTransaction?.({
      ...createEmptyTxnDraft(),
      txn_type: TXN_TYPE.WAIVER,
      amount: null,
      is_partial: false,
      reason: null
    });
  };

  const handleRemoveWaiver = async () => {
    if (!waiverTxn) return;
    await onDeleteTransaction?.(waiverTxn.id);
  };

  const txnKindOptions = TXN_KIND_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
  const visibleTxns = txns.filter((t) => t.txn_type !== TXN_TYPE.WAIVER);
  const txnsHaveAmounts = visibleTxns.some(
    (t) => t.amount != null && t.amount !== '' && Number.isFinite(Number(t.amount))
  );
  const txnNetSum = computeNetFromTransactions(visibleTxns);

  const renderSavedTxn = (t, { muted = false, showRemove = true } = {}) => {
    const kind = txnToKind(t);
    return (
      <li key={t.id} className={`pay-editor-txn-card pay-editor-txn-card--saved${muted ? ' pay-editor-txn-card--muted' : ''}`}>
        <TxnRowView
          kind={kind}
          txnType={t.txn_type}
          amount={t.amount}
          occurredOn={t.occurred_on}
          createdAt={t.created_at}
          method={t.method}
          notes={t.notes}
          currency={t.currency || currency}
          showRemove={showRemove}
          removing={saving}
          onRemove={showRemove ? () => onDeleteTransaction?.(t.id) : undefined}
        />
      </li>
    );
  };

  const hasChargeAmount = amountDue != null && Number(amountDue) > 0;

  return (
    <ProfileFormSheet
      open={open}
      onClose={onClose}
      title={title}
      subtext={composedSubtext || subtitle}
      titleId="payment-charge-editor-title"
      closeDisabled={saving}
      dismissOnOverlay={false}
      panelClassName="profile-sheet-panel--payment"
    >
      <div className="pay-invoice-card">
        <div className="pay-invoice-icon" aria-hidden>
          <InvoiceIcon />
        </div>
        <div className="pay-invoice-body">
          <div className="pay-invoice-name">{chargeName}</div>
          {charge?.charge_code ? (
            <div className="pay-invoice-charge-code">{charge.charge_code}</div>
          ) : null}
          {hasChargeAmount && Number(netPaid) > 0 ? (
            <div className="pay-invoice-received">
              {formatPaymentAmount(netPaid, currency)} received
            </div>
          ) : !hasChargeAmount ? (
            <div className="pay-invoice-tracking">Yes / no tracking</div>
          ) : (
            <div className="pay-invoice-received pay-invoice-received--none">No payments yet</div>
          )}
        </div>
        <div className="pay-invoice-aside">
          {hasChargeAmount && (
            <div className="pay-invoice-charge">
              <span className="pay-invoice-charge-label">Charge</span>
              <span className="pay-invoice-charge-value">{formatPaymentAmount(amountDue, currency)}</span>
            </div>
          )}
          <span
            className="pay-editor-status-pill"
            style={{ background: statusMeta.bg, borderColor: statusMeta.border, color: statusMeta.color }}
          >
            {getPaymentStatusLabel(charge?.status)}
          </span>
        </div>
      </div>

      <p className="pay-editor-derived-note">
        Status is calculated from the transactions below.
      </p>

      {isWaived ? (
        <section className="pay-editor-section">
          <div className="pay-editor-waived-box">
            <span>This charge is waived (Not Applicable).</span>
            <button
              type="button"
              className="pay-editor-waive-btn pay-editor-waive-btn--restore"
              onClick={handleRemoveWaiver}
              disabled={saving || !waiverTxn}
            >
              <span className="pay-editor-waive-btn__glyph" aria-hidden>↩</span>
              Remove waiver
            </button>
          </div>
          {visibleTxns.length > 0 && (
            <ul className="pay-editor-txn-list pay-editor-txn-list--muted">
              {txnsHaveAmounts && (
                <li className="pay-editor-txn-sum">
                  Sum{' '}
                  <strong>{formatPaymentAmount(txnNetSum, currency)}</strong>
                </li>
              )}
              {visibleTxns.map((t) => renderSavedTxn(t, { muted: true, showRemove: false }))}
            </ul>
          )}
        </section>
      ) : (
        <section className="pay-editor-section">
          <div className="pay-editor-section-bar">
            <div className="pay-editor-section-bar-main">
              <h3 className="pay-editor-section-title">Transactions</h3>
              {txnsHaveAmounts && !loading && (
                <span className="pay-editor-txn-sum pay-editor-txn-sum--inline">
                  Sum <strong>{formatPaymentAmount(txnNetSum, currency)}</strong>
                </span>
              )}
            </div>
            {canQuickSet && (
              <button
                type="button"
                className="pay-editor-waive-btn"
                onClick={handleWaive}
                disabled={saving}
              >
                <span className="pay-editor-waive-btn__glyph" aria-hidden>⊘</span>
                Waive
              </button>
            )}
          </div>

          {loading ? (
            <p className="pay-editor-empty">Loading…</p>
          ) : (
            <ul className="pay-editor-txn-list">
              <li className="pay-editor-txn-list-add">
                <AddTransactionRow onClick={startDraft} disabled={saving || !!pendingDraft} />
              </li>

              {pendingDraft && (
                <li className="pay-editor-txn-card pay-editor-txn-card--draft">
                  <div className="pay-editor-txn-draft-head">
                    <BadgePicker
                      label={getTxnKindLabel(draftToTxnKind(pendingDraft))}
                      badgeClass={txnKindBadgeClass(draftToTxnKind(pendingDraft))}
                      options={txnKindOptions}
                      value={draftToTxnKind(pendingDraft)}
                      onChange={(opt) => setPendingKind(opt.value)}
                      disabled={saving}
                      ariaLabel="Transaction type"
                    />
                    {draftValidation?.notice && (
                      <div
                        className={`pay-editor-inline-notice pay-editor-inline-notice--${draftValidation.notice.tone}`}
                        role="status"
                      >
                        <span>{draftValidation.notice.text}</span>
                        {draftValidation.notice.action === 'switch_full' && (
                          <button
                            type="button"
                            className="pay-editor-inline-notice-action"
                            disabled={saving}
                            onClick={() => setPendingKind(TXN_KIND.FULL_PAYMENT)}
                          >
                            Final/Full
                          </button>
                        )}
                        {draftValidation.notice.action === 'switch_partial' && (
                          <button
                            type="button"
                            className="pay-editor-inline-notice-action"
                            disabled={saving}
                            onClick={() => setPendingKind(TXN_KIND.PARTIAL_PAYMENT)}
                          >
                            Partial
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <TxnRowEdit
                    draft={pendingDraft}
                    saving={saving}
                    isAmountMode={isAmountMode}
                    remaining={remaining}
                    currency={currency}
                    onChange={updatePending}
                    onFillRemaining={fillRemaining}
                  />
                  <div className="pay-editor-record-footer">
                    <button
                      type="button"
                      className="profile-sheet-btn profile-sheet-btn--secondary pay-editor-cancel-btn"
                      onClick={() => setPendingDraft(null)}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="profile-sheet-btn profile-sheet-btn--primary pay-editor-save-btn"
                      onClick={handleSaveDraft}
                      disabled={saving || draftValidation?.canSave === false}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </li>
              )}

              {visibleTxns.map((t) => renderSavedTxn(t))}
            </ul>
          )}
        </section>
      )}
    </ProfileFormSheet>
  );
}
